import { Card } from '@/components/ui/card';
import type { DrillSide } from '@/types/api';
import { cn } from '@/utils/cn';
import { Dropzone } from './Dropzone';

export const DRILL_SIDES: DrillSide[] = ['+Z', '-Z', '+Y', '-Y', '+X', '-X'];

export interface DirStats {
  fileCount: number;
  collisionCount?: number;
}

interface ProgramFilesTabsProps {
  activeDir: DrillSide;
  onDirChange: (dir: DrillSide) => void;
  dirStats: Record<DrillSide, DirStats>;
  onDrop: (files: File[]) => void;
  isUploading?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}

export const ProgramFilesTabs = ({
  activeDir,
  onDirChange,
  dirStats,
  onDrop,
  isUploading,
  disabled,
  children,
}: ProgramFilesTabsProps): React.ReactElement => {
  return (
    <Card className="p-0 gap-0">
      <div className="flex border-b-2 border-slate-200">
        {DRILL_SIDES.map((dir) => {
          const { fileCount, collisionCount } = dirStats[dir];
          return (
            <button
              key={dir}
              type="button"
              className={cn(
                'flex items-center gap-1.5 border-b-2 px-4 py-2.5 font-medium transition-colors cursor-pointer',
                activeDir === dir
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-slate-500 hover:bg-slate-50 hover:text-slate-700'
              )}
              onClick={() => onDirChange(dir)}
            >
              <span>{dir}</span>
              {fileCount > 0 && <span className="text-sm text-slate-400">({fileCount})</span>}
              {(collisionCount ?? 0) > 0 && <span className="ml-1 text-sm text-red-600">⚠ {collisionCount}</span>}
            </button>
          );
        })}
      </div>

      <div className="flex flex-col">
        {children}
        <Dropzone
          className="m-4"
          accept={{ 'text/x-c': ['.h', '.H'] }}
          multiple={true}
          onDrop={onDrop}
          isLoading={isUploading}
          disabled={disabled}
          hint={`.H (${activeDir})`}
        />
      </div>
    </Card>
  );
};
